/**
 * The lucky draw's sound — the countdown, the wheel, the winner and the
 * disqualification.
 *
 * **One recording, three synthesised.** The wheel is the Committee's own
 * sample — a bicycle freewheel coasting, which is what a prize wheel actually
 * is. The countdown, the winner and the disqualification are built from
 * oscillators at the moment they play.
 *
 * The three stay synthesised because they are short, they have to be exactly
 * on the beat of something already happening on screen, and nothing has to
 * finish downloading before they will work. The hall's laptop opens `/screen`
 * and is left alone; a sound still loading when Admin presses "Draw winner"
 * is a sound that does not play, and audio that works in rehearsal but not on
 * stage is worse than none.
 *
 * The wheel earns its exception: a recording carries the irregularity of real
 * teeth, which a click train never does. It is pre-loaded the moment sound is
 * switched on, and falls back to a synthesised ratchet if the file cannot be
 * fetched — so a missing asset costs fidelity, not silence.
 *
 * **Browsers will not make noise unaccompanied.** An `AudioContext` starts
 * suspended until a real user gesture, so `unlock()` has to be called from a
 * click or a key press — see `SoundToggle` on the screen, which is both the
 * control and the gesture.
 */

const STORE_KEY = 'sdg.screen.sound'

let context: AudioContext | null = null
let master: GainNode | null = null
/** Default off: the hall decides when the projector starts making noise. */
let enabled = false
const listeners = new Set<() => void>()

function announce() {
  for (const fn of listeners) fn()
}

/** Re-render anything showing the sound state. */
export function onSoundChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function soundEnabled(): boolean {
  return enabled
}

/** True once a gesture has actually started the audio engine. */
export function soundReady(): boolean {
  return context?.state === 'running'
}

/**
 * Starts (or resumes) the engine. **Must be called from a user gesture.**
 *
 * Safe to call repeatedly — a context that is already running is left alone,
 * which is what makes it usable as a blanket "any click re-arms the audio"
 * handler after a tab is backgrounded and the browser suspends it.
 */
export async function unlock(): Promise<void> {
  if (!context) {
    const Ctor = window.AudioContext ?? (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return
    context = new Ctor()
    master = context.createGain()
    master.gain.value = 0.9
    master.connect(context.destination)
  }
  if (context.state === 'suspended') await context.resume()

  /*
   * Fetch and decode the wheel now, not on the first spin.
   *
   * Sound is switched on during setup and the first draw is usually an hour
   * later, so by then it is long since ready. Leaving it until `wheelStart`
   * meant the very first spin of the day raced the download — the one spin
   * where nobody would think to check.
   */
  preloadSamples()

  announce()
}

export async function setSoundEnabled(on: boolean): Promise<void> {
  enabled = on
  try {
    localStorage.setItem(STORE_KEY, on ? 'on' : 'off')
  } catch {
    // Private browsing. The choice simply does not survive a reload.
  }
  if (on) await unlock()
  announce()
}

/** Restores the previous choice. The engine still waits for a gesture. */
export function restoreSoundPreference(): void {
  try {
    enabled = localStorage.getItem(STORE_KEY) === 'on'
  } catch {
    enabled = false
  }
}

// ── Voices ────────────────────────────────────────────────────

/**
 * One shaped tone.
 *
 * Every sound here is a handful of these. The envelope is deliberately short
 * with a fast attack: a hall is reverberant, and anything with a slow onset
 * arrives as a smear rather than as a beat.
 */
function tone(opts: {
  freq: number
  /** Seconds from now. */
  at?: number
  duration: number
  type?: OscillatorType
  gain?: number
  /** Slides to this frequency across the note — used by the disqualification. */
  glideTo?: number
}) {
  if (!context || !master) return

  const start = context.currentTime + (opts.at ?? 0)
  const osc = context.createOscillator()
  const env = context.createGain()

  osc.type = opts.type ?? 'triangle'
  osc.frequency.setValueAtTime(opts.freq, start)
  if (opts.glideTo != null) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, opts.glideTo), start + opts.duration)
  }

  const peak = opts.gain ?? 0.3
  env.gain.setValueAtTime(0.0001, start)
  env.gain.exponentialRampToValueAtTime(peak, start + 0.012)
  env.gain.exponentialRampToValueAtTime(0.0001, start + opts.duration)

  osc.connect(env)
  env.connect(master)
  osc.start(start)
  osc.stop(start + opts.duration + 0.02)
}

/** A short burst of filtered noise — one tooth of the fallback ratchet. */
function click(gain: number, at = 0) {
  if (!context || !master) return

  const start = context.currentTime + at
  const length = Math.floor(context.sampleRate * 0.03)
  const buffer = context.createBuffer(1, length, context.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < length; i++) {
    // Decaying white noise: a pawl striking a tooth, not a hiss.
    data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3
  }

  const source = context.createBufferSource()
  source.buffer = buffer

  const band = context.createBiquadFilter()
  band.type = 'bandpass'
  band.frequency.value = 2200
  band.Q.value = 1.4

  const env = context.createGain()
  env.gain.value = gain

  source.connect(band)
  band.connect(env)
  env.connect(master)
  source.start(start)
}

function canPlay(): boolean {
  return enabled && context?.state === 'running'
}

// ── The wheel ─────────────────────────────────────────────────

/**
 * The spin is a real recording: a bicycle freewheel coasting.
 *
 * `client/public/sounds/wheel.mp3` — "bike back wheel coasting", from the
 * Freesound community, supplied by the Committee. It is the right sound
 * because a prize wheel *is* a ratchet, and a recording of one carries the
 * irregularity that a synthesised click train never does.
 *
 * It is played as one continuous sound whose **playback rate falls** across
 * the spin, rather than as one click per name dealt. That is how a freewheel
 * actually behaves as it slows, and it keeps the audio locked to the wheel's
 * deceleration instead of to a separate timer that would drift away from it.
 */
const SAMPLES = {
  wheel: '/sounds/wheel.mp3',
  countdown: '/sounds/countdown.mp3',
  winner: '/sounds/winner.mp3',
  disqualified: '/sounds/disqualified.mp3',
  /**
   * Every reveal in the award ceremony, whichever award and whichever place.
   *
   * `award-2.mp3`, `award-3.mp3` and `attire.mp3` are still in
   * `public/sounds` from earlier rounds but are no longer fetched.
   */
  award: '/sounds/award-1.mp3',
} as const

/**
 * How the two recordings are fitted to what is on screen.
 *
 * **Tune these by ear.** They are the only numbers in this file that depend on
 * the specific recordings in `public/sounds`, and swapping either file is
 * likely to need one of them changed. Everything else is generic.
 */
const FIT = {
  /**
   * Playback rate at the moment the wheel is released, and where it ends.
   *
   * The recording is a bicycle freewheel *coasting*, which ticks far slower
   * than a prize wheel thrown hard — played at its natural 1.0 it sounds like
   * the wheel is already stopping. So the spin starts well above natural
   * speed and lands at roughly natural speed, rather than starting at natural
   * and dropping below it.
   *
   * Raise `spinFrom` if the release still sounds sluggish; raise `spinTo` if
   * the landing sounds like it grinds to a halt.
   */
  spinFrom: 2.6,
  spinTo: 1.0,

  /**
   * How loud the wheel is, against the other voices.
   *
   * The loudest thing in the draw by a distance, deliberately: it runs for
   * seven seconds under an otherwise silent screen, where the countdown tick
   * and the fanfare are short punctuation either side of it. At 0.5 it sat
   * under the room.
   *
   * **Above 1.0 is amplification**, not just "full" — a gain node will
   * happily push past unity. The recording is nowhere near full scale so
   * there is headroom, but if the wheel crackles through the hall's PA this
   * is the number to bring down first.
   */
  spinVolume: 1.5,

  /**
   * How long the wheel takes to fade at the end of the spin.
   *
   * Short. It was 0.35s, which audibly pulled the sound away before the
   * wheel had stopped — the ratchet should still be going as the pointer
   * settles, then stop, not dissolve into the landing.
   */
  spinFadeS: 0.12,

  /**
   * How long the disqualification clip takes to fade at its end.
   *
   * It is eight seconds long and stops dead on its last sample, which lands
   * as a click through a hall PA and leaves the room in an abrupt silence —
   * the wrong punctuation for the one announcement that is already awkward.
   * Long enough to read as the sound receding rather than being cut.
   *
   * Scheduled against `buffer.duration` rather than a written-down length, so
   * replacing the file does not silently fade the wrong part of it.
   */
  disqualifiedFadeS: 0.8,
} as const

type SampleName = keyof typeof SAMPLES

const buffers: Partial<Record<SampleName, AudioBuffer | null>> = {}
const loading: Partial<Record<SampleName, Promise<void>>> = {}

let wheelSource: AudioBufferSourceNode | null = null
let wheelGain: GainNode | null = null
let awardSource: AudioBufferSourceNode | null = null
let awardGain: GainNode | null = null

/** Fetched once each, after the gesture that gives us a context to decode with. */
function loadSample(name: SampleName): Promise<void> {
  const existing = loading[name]
  if (existing) return existing

  const task = (async () => {
    try {
      const res = await fetch(SAMPLES[name])
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      buffers[name] = await context!.decodeAudioData(await res.arrayBuffer())
    } catch (error) {
      // Not fatal. Each voice has a synthesised fallback, so a missing or
      // unreadable file costs fidelity rather than silence on stage.
      console.warn(`[stage audio] "${name}" sample unavailable, using the synthesised voice:`, error)
      buffers[name] = null
    }
  })()

  loading[name] = task
  return task
}

export function preloadSamples(): void {
  if (!context) return
  for (const name of Object.keys(SAMPLES) as SampleName[]) void loadSample(name)
}


function fadeOut(
  source: AudioBufferSourceNode | null,
  gain: GainNode | null,
  fadeS = 0.12,
): void {
  if (!context || !source || !gain) return
  const now = context.currentTime
  gain.gain.cancelScheduledValues(now)
  gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), now)
  gain.gain.exponentialRampToValueAtTime(0.0001, now + fadeS)
  try {
    source.stop(now + fadeS + 0.02)
  } catch {
    // Already stopped — a second announcement landing on the tail.
  }
}

/**
 * The fallback: scheduled clicks on the same decelerating curve.
 *
 * Every click is booked on the audio clock in one go rather than fired from a
 * timer, so a stalled frame cannot leave a gap in the middle of the spin.
 */
function syntheticWheel(durationS: number) {
  let at = 0
  while (at < durationS) {
    const progress = at / durationS
    click(0.26 - 0.1 * progress, at)
    at += 0.07 + 0.38 * progress * progress
  }
}


/**
 * The lucky draw's four sounds.
 *
 * Every one is a no-op unless sound is switched on *and* a gesture has
 * started the engine, so callers can fire them unconditionally and the
 * silence costs nothing.
 */
export const stageSound = {
  /**
   * One tick as each new second starts.
   *
   * The Committee's own 0.94s recording, fired on the change of the number on
   * the dial — so the sound and the number cannot drift apart, however long
   * the countdown is. That is why this is a one-shot rather than a clip
   * stretched across the whole count.
   */
  countdownTick(secondsLeft = 5) {
    if (!canPlay() || !context || !master) return

    const buffer = buffers.countdown
    if (!buffer) {
      // No recording: a synthesised blip, brighter on the final second.
      if (secondsLeft <= 1) {
        tone({ freq: 1046.5, duration: 0.42, type: 'triangle', gain: 0.34 })
        tone({ freq: 1568, duration: 0.42, type: 'sine', gain: 0.12 })
      } else {
        tone({ freq: secondsLeft <= 3 ? 880 : 660, duration: 0.16, type: 'triangle', gain: 0.26 })
      }
      return
    }

    const now = context.currentTime
    const source = context.createBufferSource()
    source.buffer = buffer

    const gain = context.createGain()
    gain.gain.value = 0.62
    /*
     * Cut at one second so a tick can never overlap the next one. The clip is
     * 0.94s, so this only bites if a longer one is dropped in later — and two
     * ticks ringing together would blur exactly the beat they exist to mark.
     */
    gain.gain.setValueAtTime(0.62, now + 0.88)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.98)

    source.connect(gain)
    gain.connect(master)
    source.start(now)
    source.stop(now + 1)
  },

  /**
   * The wheel, for the length of the spin.
   *
   * The recording loops — it is 21 seconds of coasting against a 5-second
   * spin — and its playback rate falls from `FIT.spinFrom` to `FIT.spinTo`
   * across the spin, which is what makes it read as a wheel running down
   * rather than a clip being cut off. It starts from a random point so two
   * draws in a row do not begin on the same tooth.
   */
  wheelStart(durationMs = 5000) {
    if (!canPlay() || !context || !master) return
    const durationS = durationMs / 1000

    void loadSample('wheel').then(() => {
      // The spin may already have finished while the file was decoding —
      // only ever on the very first draw, and silence beats a ratchet that
      // starts after the winner has landed.
      if (!canPlay() || !context || !master || wheelSource) return

      const buffer = buffers.wheel
      if (!buffer) {
        syntheticWheel(durationS)
        return
      }

      const now = context.currentTime
      const source = context.createBufferSource()
      source.buffer = buffer
      source.loop = true

      /*
       * Fast at the release, natural at the landing — not natural down to a
       * third, which made the spin sound like it was already stopping the
       * moment it started. A coasting freewheel ticks far slower than a
       * prize wheel thrown hard.
       */
      source.playbackRate.setValueAtTime(FIT.spinFrom, now)
      source.playbackRate.exponentialRampToValueAtTime(FIT.spinTo, now + durationS)

      const gain = context.createGain()
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(FIT.spinVolume, now + 0.08)
      // Held at full until the very end, then cut quickly — see spinFadeS.
      gain.gain.setValueAtTime(FIT.spinVolume, now + Math.max(0.05, durationS - FIT.spinFadeS))
      gain.gain.exponentialRampToValueAtTime(0.0001, now + durationS)

      source.connect(gain)
      gain.connect(master)
      /*
       * From the top, every spin.
       *
       * This used to start at a random point so two draws running back to
       * back would not begin on the same tooth. The Committee prefers the
       * recording's own opening every time — it has an attack that reads as
       * the wheel being thrown, and starting mid-coast threw that away.
       */
      source.start(now)
      source.stop(now + durationS + 0.05)

      wheelSource = source
      wheelGain = gain
      source.onended = () => {
        if (wheelSource === source) {
          wheelSource = null
          wheelGain = null
        }
      }
    })
  },

  /** Cut the wheel short — the spin was cancelled or the screen changed. */
  wheelStop() {
    fadeOut(wheelSource, wheelGain)
    wheelSource = null
    wheelGain = null
  },

  /**
   * An award place being announced — the same fanfare for every one.
   *
   * **Every reveal in the ceremony uses it** — third, second and first, the
   * single-winner awards (Best Cultural Performance, Most Active School,
   * Most Active Club) and both categories of Best Traditional Attire.
   *
   * Three sounds were tried first, one per place, then one each for attire.
   * One for all of them is better: a sound per place made the *ranking* the
   * subject, when the moment belongs to whoever is being announced.
   *
   * Any previous one is faded first, so a Committee moving briskly between
   * places does not stack two on top of each other.
   */
  awardReveal() {
    if (!canPlay() || !context || !master) return

    void loadSample('award').then(() => {
      if (!canPlay() || !context || !master) return

      const buffer = buffers.award
      if (!buffer) {
        // No recording for this place: the generic winner voice.
        stageSound.winner()
        return
      }

      fadeOut(awardSource, awardGain, 0.25)

      const now = context.currentTime
      const source = context.createBufferSource()
      source.buffer = buffer

      const gain = context.createGain()
      gain.gain.value = 0.75

      source.connect(gain)
      gain.connect(master)
      source.start(now)

      awardSource = source
      awardGain = gain
      source.onended = () => {
        if (awardSource === source) {
          awardSource = null
          awardGain = null
        }
      }
    })
  },

  /**
   * The winner — the Committee's brass fanfare with timpani.
   *
   * Fired the instant the wheel stops, not when the card appears: the two are
   * two seconds apart (`drawHoldMs`), and waiting for the card left the hall
   * in silence for exactly the beat where the announcement belongs. The
   * fanfare runs under the hold and is still playing when the card lands.
   */
  winner() {
    if (!canPlay() || !context || !master) return

    const buffer = buffers.winner
    if (!buffer) {
      // No recording: a bright rising arpeggio over the confetti.
      const notes = [523.25, 659.25, 783.99, 1046.5]
      notes.forEach((freq, i) => {
        tone({ freq, at: i * 0.085, duration: 0.5, type: 'triangle', gain: 0.3 })
        tone({ freq: freq * 2, at: i * 0.085, duration: 0.38, type: 'sine', gain: 0.1 })
      })
      // A held fifth underneath, so it reads as a chord rather than four blips.
      tone({ freq: 261.63, at: 0.26, duration: 0.9, type: 'sine', gain: 0.18 })
      tone({ freq: 392, at: 0.26, duration: 0.9, type: 'sine', gain: 0.14 })
      return
    }

    const now = context.currentTime
    const source = context.createBufferSource()
    source.buffer = buffer

    const gain = context.createGain()
    gain.gain.value = 0.75

    source.connect(gain)
    gain.connect(master)
    source.start(now)
  },

  /**
   * Called three times and not present — slide 14.
   *
   * Falling, detuned and short. Deliberately not a comic failure buzz: a
   * student's name is on a 3-metre screen while this plays, and the sound has
   * to say "we are moving on" rather than laugh at them.
   */
  disqualified() {
    if (!canPlay() || !context || !master) return

    const buffer = buffers.disqualified
    if (!buffer) {
      // No recording: two short falling detuned tones.
      tone({ freq: 392, duration: 0.55, type: 'sawtooth', gain: 0.16, glideTo: 196 })
      tone({ freq: 389, at: 0.02, duration: 0.55, type: 'sawtooth', gain: 0.12, glideTo: 194 })
      return
    }

    const now = context.currentTime
    const source = context.createBufferSource()
    source.buffer = buffer

    /*
     * Full level until the tail, then down to silence on the last sample.
     *
     * `Math.min` against a third of the clip so a much shorter replacement
     * recording fades proportionally instead of starting its fade before it
     * has begun. `exponentialRampToValueAtTime` cannot reach a true zero, so
     * it targets 0.0001 — inaudible — as everywhere else in this file.
     */
    const length = buffer.duration
    const fade = Math.min(FIT.disqualifiedFadeS, length / 3)

    const gain = context.createGain()
    gain.gain.setValueAtTime(0.6, now)
    gain.gain.setValueAtTime(0.6, now + length - fade)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + length)

    source.connect(gain)
    gain.connect(master)
    source.start(now)
  },
}
